import React from "react";
import { Modal, Form, InputNumber, Row, Col, Typography } from "antd";
import { InputField, AntdButton } from "../../../blueplanitUtils/InputField";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { S3ACCESSURL } from "../../../databucket";


const ViewEditMaintenanceModal = ({ editModalOpen, viewModalOpen, editingRecord, viewingRecord, onCloseEdit, onCloseView, onUpdate, setEditingRecord, handleFileUpload, }) => {

  const disable = useSelector((state) => state.employee.button);

  // Fields to show in Maintenance History table
  const maintenanceChangedFields = [
    "productName",
    "description",
    "maintenanceCost",
    "billPhoto"
  ];


  const maintenanceColumns = [
    {
      title: "Updated By",
      dataIndex: "editedBy",
      align: "center",
    },
    {
      title: "Updated Time",
      dataIndex: "editedAt",
      align: "center",
      width: 180,
      render: (t) =>
        moment(Number(t)).isValid()
          ? moment(Number(t)).format("DD/MM/YYYY HH:mm")
          : "—"
    },
    ...maintenanceChangedFields.map((field) => ({
      title: field.charAt(0).toUpperCase() + field.slice(1),
      key: field,
      align: "center",
      // Inside your render function for each field:
      render: (_, record) => {
        const change = record.changes?.find(c => c.field === field);
        let oldValue = change?.oldValue ?? record[field];
        let newValue = change?.newValue ?? record[field];

        // Helper functions must be defined first
        const formatCost = (val) =>
          val != null && val !== "" ? `₹ ${parseFloat(val).toFixed(0)}` : "—";

        const formatPhotos = (photos) =>
          Array.isArray(photos) && photos.length > 0
            ? photos.map((file, idx) => (
              <a
                key={idx}
                href={`${S3ACCESSURL}${file.url || file.data}`}
                target="_blank"
                rel="noreferrer"
                style={{ display: "block" }}
              >
                {file.originalName || `File ${idx + 1}`}
              </a>
            ))
            : "No File";

        // Fallback to snapshot if no change recorded
        if (!change) {
          if (field === "maintenanceCost") {
            return <span>{formatCost(record[field])}</span>;
          }
          if (field === "billPhoto") {
            return <span>{formatPhotos(record[field])}</span>;
          }
          return <span>{record[field] || "No Changes"}</span>;
        }

        // For bill photo
        if (field === "billPhoto") {
          return (
            <div>
              <div style={{ color: "red" }}>{formatPhotos(oldValue)}</div>
              <span style={{ margin: "0 4px" }}>→</span>
              <div style={{ color: "green" }}>{formatPhotos(newValue)}</div>
            </div>
          );
        }

        // For maintenance cost
        if (field === "maintenanceCost") {
          return (
            <div>
              <span style={{ color: "red" }}>{formatCost(oldValue)}</span>
              <span style={{ margin: "0 4px" }}>→</span>
              <span style={{ color: "green" }}>{formatCost(newValue)}</span>
            </div>
          );
        }
        // Default text fields
        return (
          <div>
            <span style={{ color: "red" }}>{oldValue || "—"}</span>
            <span style={{ margin: "0 4px" }}>→</span>
            <span style={{ color: "green" }}>{newValue || "—"}</span>
          </div>
        );
      }
    }))
  ];

  return (
    <>
      {/* EDIT MODAL */}
      <Modal
        title={`Edit Maintenance - Asset ${editingRecord?.assetTagId}`}
        open={editModalOpen}
        onCancel={onCloseEdit}
        onOk={onUpdate}
        okButtonProps={{ disabled: disable }}
      >
        {editingRecord && (
          <Form layout="vertical">

            <Form.Item label="Product Name">
              <InputField
                value={editingRecord.productName}
                onChange={(e) =>
                  setEditingRecord((prev) => ({
                    ...prev,
                    productName: e.target.value,
                  }))
                }
              />
            </Form.Item>

            <Form.Item label="Product Details">
              <InputField
                value={editingRecord.description}
                onChange={(e) =>
                  setEditingRecord((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Form.Item>

            <Form.Item label="Maintenance Cost">
              <InputNumber
                style={{ width: "100%" }}
                value={editingRecord.maintenanceCost}
                onChange={(value) =>
                  setEditingRecord((prev) => ({
                    ...prev,
                    maintenanceCost: value,
                  }))
                }
              />
            </Form.Item>

            <Form.Item label="Maintenance Bill Photo">
              <AntdButton
                label={
                  editingRecord.billPhoto
                    ? "Change Bill Photo"
                    : "Upload Bill Photo"
                }
                icon={<FontAwesomeIcon icon={faUpload} />}
                onClick={() =>
                  document.getElementById("bill-photo-upload").click()
                }
              />
              <InputField
                id="bill-photo-upload"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />

              {Array.isArray(editingRecord.billPhoto) &&
                editingRecord.billPhoto.map((file, index) => (
                  <a
                    key={index}
                    href={`${S3ACCESSURL}${file.url || file.data}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "block",
                      color: "#1890ff",
                      marginTop: 8,
                    }}
                  >
                    {file.originalName}
                  </a>
                ))}
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        title="Maintenance Details"
        open={viewModalOpen}
        footer={null}
        onCancel={onCloseView}
        width={900}
      >
        {viewingRecord && (
          <>
            <div
              style={{
                backgroundColor: "#f9f9f9",
                padding: "24px",
                borderRadius: "8px",
                marginBottom: "24px"
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <p>
                    <strong>Asset ID:</strong> {viewingRecord.assetTagId || "—"}
                  </p>
                  <p>
                    <strong>Product Name:</strong>{" "}
                    {viewingRecord.productName || "—"}
                  </p>
                  <p>
                    <strong>Checked Out Person:</strong>{" "}
                    {viewingRecord.checkedOutPerson || "—"}
                  </p>
                  <p>
                    <strong>Reason:</strong> {viewingRecord.reason || "—"}
                  </p>
                  <p>
                    <strong>Checked Out Date:</strong>{" "}
                    {viewingRecord.checkedOutDate
                      ? new Date(
                        Number(viewingRecord.checkedOutDate)
                      ).toLocaleDateString()
                      : "—"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Product Details:</strong>{" "}
                    {viewingRecord.description || "—"}
                  </p>
                  <p>
                    <strong>Maintenance Cost:</strong>{" "}
                    {viewingRecord.maintenanceCost != null
                      ? `₹ ${parseFloat(viewingRecord.maintenanceCost).toFixed(
                        0
                      )}`
                      : "—"}
                  </p>
                  <p>
                    <strong>Expected Check-In Date:</strong>{" "}
                    {viewingRecord.expectedCheckInDate
                      ? new Date(
                        Number(viewingRecord.expectedCheckInDate)
                      ).toLocaleDateString()
                      : "—"}
                  </p>
                  <p>
                    <strong>Bill Photo:</strong>{" "}
                    {viewingRecord.billPhoto && viewingRecord.billPhoto.length > 0 ? (
                      viewingRecord.billPhoto.map((file, index) => (
                        <a
                          key={index}
                          href={S3ACCESSURL + file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: "block", color: "#1890ff" }}
                        >
                          {file.originalName || `File ${index + 1}`}
                        </a>
                      ))
                    ) : (
                      "No File"
                    )}
                  </p>
                </Col>
              </Row>
            </div>

            <Typography.Title level={4}>Maintenance History</Typography.Title>
            <CustomGridTable
              columns={maintenanceColumns}
              Id="maintenance-history-table"
              dataSource={viewingRecord.maintenanceHistory || []}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
              locale={{ emptyText: "No maintenance history available." }}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default ViewEditMaintenanceModal;
